/**
 * Core implementation for intelligent model routing.
 *
 * Routers are thin resources over the shared `MorphAPIClient` transport: they
 * POST to `/v1/router/<provider>` and map the response. Auth, retries, timeouts,
 * and the SDK-version header all live in the transport.
 */
import { MorphAPIClient } from '../core/client.js';
import { APIResource } from '../core/resource.js';
import { MorphError } from '../tools/utils/resilience.js';
import { logger } from '../logger.js';
import type {
  RouterConfig,
  RouterInput,
  RouterResult,
  RawRouterResult,
  ComplexityLevel,
  Provider,
  ClassifyInput,
  ClassifyResult,
} from './types.js';

const DEFAULT_API_URL = 'https://api.morphllm.com';
const DEFAULT_TIMEOUT = 5000; // 5 seconds (responses typically <500ms)

function resolveClient(clientOrConfig: MorphAPIClient | RouterConfig): MorphAPIClient {
  if (clientOrConfig instanceof MorphAPIClient) return clientOrConfig;
  return new MorphAPIClient({
    apiKey: clientOrConfig.apiKey,
    baseURL: clientOrConfig.apiUrl ?? DEFAULT_API_URL,
    timeout: clientOrConfig.timeout ?? DEFAULT_TIMEOUT,
    retryConfig: clientOrConfig.retryConfig,
    debug: clientOrConfig.debug,
  });
}

abstract class BaseRouter extends APIResource {
  protected provider: Provider;
  protected timeout: number;

  constructor(provider: Provider, clientOrConfig: MorphAPIClient | RouterConfig = {}) {
    super(resolveClient(clientOrConfig));
    this.provider = provider;
    const cfg = clientOrConfig instanceof MorphAPIClient ? {} : clientOrConfig;
    this.timeout = cfg.timeout ?? DEFAULT_TIMEOUT;
  }

  /** Throw the historical "API key is required" message when no key is resolvable. */
  protected requireApiKey(): void {
    if (!this._client.resolveApiKey()) {
      throw new Error(
        'Morph API key is required. Set MORPH_API_KEY environment variable or pass apiKey in config.',
      );
    }
  }

  /** POST to the router and surface failures as `Router API error (<status>): ...`. */
  protected async route<T>(provider: string, input: RouterInput): Promise<T> {
    try {
      return await this._client.post<T>(`/v1/router/${provider}`, {
        body: { input: input.input, mode: input.mode || 'balanced' },
        timeout: this.timeout,
      });
    } catch (error) {
      if (error instanceof MorphError) {
        throw new Error(`Router API error (${error.statusCode}): ${error.message}`);
      }
      throw error;
    }
  }

  /**
   * Select the optimal model for a given input and mode
   */
  async selectModel(input: RouterInput): Promise<RouterResult> {
    this.requireApiKey();
    logger.debug('ModelRouter', 'request', { provider: this.provider, mode: input.mode || 'balanced' });
    const apiResult = await this.route<{ model: string; confidence?: number }>(this.provider, input);
    logger.debug('ModelRouter', 'selected', { provider: this.provider, model: apiResult.model });
    return { model: apiResult.model };
  }
}

/**
 * OpenAI model router for GPT-5 series
 *
 * @deprecated Prefer `MorphClient` (`new MorphClient({ apiKey }).routers.openai`). In edge
 * runtimes where `MorphClient` is unavailable, pass a shared transport:
 * `new OpenAIRouter(new MorphAPIClient({ apiKey }))`. Kept only for backwards compatibility.
 */
export class OpenAIRouter extends BaseRouter {
  constructor(clientOrConfig: MorphAPIClient | RouterConfig = {}) {
    super('openai', clientOrConfig);
  }

  /**
   * Select optimal GPT-5 model
   *
   * @param input - User input and mode
   * @returns Selected model name (gpt-5-mini | gpt-5-low | gpt-5-medium | gpt-5-high)
   */
  async selectModel(input: RouterInput): Promise<RouterResult> {
    return super.selectModel(input);
  }
}

/**
 * Anthropic model router for Claude 4.5 series
 *
 * @deprecated Prefer `MorphClient` (`new MorphClient({ apiKey }).routers.anthropic`). In edge
 * runtimes where `MorphClient` is unavailable, pass a shared transport:
 * `new AnthropicRouter(new MorphAPIClient({ apiKey }))`. Kept only for backwards compatibility.
 */
export class AnthropicRouter extends BaseRouter {
  constructor(clientOrConfig: MorphAPIClient | RouterConfig = {}) {
    super('anthropic', clientOrConfig);
  }

  /**
   * Select optimal Claude model
   *
   * @param input - User input and mode
   * @returns Selected model name (claude-4.5-haiku | claude-4.5-sonnet)
   */
  async selectModel(input: RouterInput): Promise<RouterResult> {
    return super.selectModel(input);
  }
}

/**
 * Google Gemini model router
 *
 * @deprecated Prefer `MorphClient` (`new MorphClient({ apiKey }).routers.gemini`). In edge
 * runtimes where `MorphClient` is unavailable, pass a shared transport:
 * `new GeminiRouter(new MorphAPIClient({ apiKey }))`. Kept only for backwards compatibility.
 */
export class GeminiRouter extends BaseRouter {
  constructor(clientOrConfig: MorphAPIClient | RouterConfig = {}) {
    super('gemini', clientOrConfig);
  }

  /**
   * Select optimal Gemini model
   *
   * @param input - User input and mode
   * @returns Selected model name (gemini-2.5-flash | gemini-2.5-pro)
   */
  async selectModel(input: RouterInput): Promise<RouterResult> {
    return super.selectModel(input);
  }
}

/**
 * Raw difficulty classification router (no provider-specific mapping)
 *
 * @deprecated Prefer `MorphClient` (`new MorphClient({ apiKey }).routers.raw`). In edge
 * runtimes where `MorphClient` is unavailable, pass a shared transport:
 * `new RawRouter(new MorphAPIClient({ apiKey }))`. Kept only for backwards compatibility.
 */
export class RawRouter extends BaseRouter {
  constructor(clientOrConfig: MorphAPIClient | RouterConfig = {}) {
    super('raw' as Provider, clientOrConfig);
  }

  /**
   * Get raw difficulty classification
   *
   * @param input - User input and mode
   * @returns Raw difficulty (easy | medium | hard | needs_info)
   */
  async classify(input: RouterInput): Promise<RawRouterResult> {
    this.requireApiKey();
    logger.debug('RawRouter', 'request', { mode: input.mode || 'balanced' });

    const apiResult = await this.route<{ model?: string; difficulty?: string; confidence?: number }>('raw', input);

    // Support both 'model' and 'difficulty' fields for compatibility.
    // Empty string from API means "medium" difficulty (API bug workaround).
    let difficulty: ComplexityLevel;
    if (apiResult.difficulty === '') {
      difficulty = 'medium';
    } else {
      difficulty = (apiResult.difficulty || apiResult.model) as ComplexityLevel;
    }

    logger.debug('RawRouter', 'classified', { difficulty });
    return { difficulty };
  }
}

/**
 * Classifier for `/v1/router/classify`.
 *
 * Returns the raw difficulty/ambiguity/domain signals without mapping them to a
 * model. The caller owns the policy — this is what `@morphllm/morphrouter` uses
 * so the routing matrix configured in the dashboard stays the source of truth.
 */
export class RouterClassifier extends APIResource {
  private timeout: number;

  constructor(clientOrConfig: MorphAPIClient | RouterConfig = {}) {
    super(resolveClient(clientOrConfig));
    const cfg = clientOrConfig instanceof MorphAPIClient ? {} : clientOrConfig;
    this.timeout = cfg.timeout ?? DEFAULT_TIMEOUT;
  }

  /** Classify an input across the requested axes (all three by default). */
  async classify(input: ClassifyInput): Promise<ClassifyResult> {
    if (!this._client.resolveApiKey()) {
      throw new Error(
        'Morph API key is required. Set MORPH_API_KEY environment variable or pass apiKey in config.',
      );
    }
    logger.debug('RouterClassifier', 'request', { classes: input.classes ?? 'all' });
    try {
      const res = await this._client.post<{ classifications: ClassifyResult }>('/v1/router/classify', {
        body: { input: input.input, ...(input.classes ? { classes: input.classes } : {}) },
        timeout: this.timeout,
      });
      logger.debug('RouterClassifier', 'classified', {
        difficulty: res.classifications.difficulty?.label,
        ambiguity: res.classifications.ambiguity?.label,
        domain: res.classifications.domain?.label,
      });
      return res.classifications;
    } catch (error) {
      if (error instanceof MorphError) {
        throw new Error(`Router API error (${error.statusCode}): ${error.message}`);
      }
      throw error;
    }
  }
}
